import type { FC } from "react";

import { Navigate, Route, Routes, useLocation } from "@shared/react-router-dom";
import { wildcardPath } from "@shared/utils";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import {
  PatientRxSignInPage,
  PatientStoreActivationPage,
  PatientStoreSignUpPage,
} from "@unauthenticated/signupSignin/scenes";
import { ForgotPasswordPage } from "@unauthenticated/signupSignin/scenes/ForgotPasswordPage/ForgotPasswordPage";

interface Props {
  csuid: string;
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  newSSOErrors?: string;
  pageVersion?: "react_with_sso" | "react";
  isPreview?: boolean;
  isStoreOwner: boolean;
  storeId?: string;
}

const Pages: FC<Props> = ({
  csuid,
  patientAttributes,
  additionalAttributes,
  newSSOErrors,
  pageVersion,
  isPreview,
  isStoreOwner,
  storeId,
}) => {
  const { storeSlug, treatmentPlanSlug, uuid, isActivated } = patientAttributes;
  const identitySlug = uuid || treatmentPlanSlug;

  const searchParams = useLocation().search;

  const getRoutes = () => {
    if (!uuid) {
      return `${authRoutes.welcome_sign_up(storeSlug)}${searchParams}`;
    }
    if (isActivated) {
      return `${authRoutes.welcome_sign_in(storeSlug, identitySlug)}${searchParams}`;
    }

    return `${authRoutes.welcome_activation(storeSlug, identitySlug)}${searchParams}`;
  };

  return (
    <Routes>
      <Route
        path={wildcardPath(authRoutes.welcome_sign_up(storeSlug))}
        element={
          <PatientStoreSignUpPage
            storeId={storeId}
            pageVersion={pageVersion}
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            isPreview={isPreview}
            isStoreOwner={isStoreOwner}
            newSSOErrors={newSSOErrors}
          />
        }
      />
      <Route
        path={wildcardPath(authRoutes.welcome_sign_in(storeSlug, identitySlug))}
        element={
          <PatientRxSignInPage
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            newSSOErrors={newSSOErrors}
          />
        }
      />
      <Route
        path={wildcardPath(authRoutes.welcome_activation(storeSlug, identitySlug))}
        element={
          <PatientStoreActivationPage
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            csuid={`${csuid}`}
            pageVersion={pageVersion}
            newSSOErrors={newSSOErrors}
          />
        }
      />
      <Route
        path={wildcardPath(authRoutes.welcome_forgot_password(storeSlug, identitySlug))}
        element={<ForgotPasswordPage />}
      />
      <Route path="*" element={<Navigate to={getRoutes()} replace />} />
    </Routes>
  );
};

export { Pages };
